import { useEffect } from 'react';
import Header from '../components/Header';
import Hero from '../components/Hero';
import SocialProofBar from '../components/SocialProofBar';
import InteractivePricingSection from '../components/InteractivePricingSection';
import ReadyForServiceCard from '../components/ReadyForServiceCard';
import Testimonials from '../components/Testimonials';
import ServiceArea from '../components/ServiceArea';
import SocialMedia from '../components/SocialMedia';
import Gallery from '../components/Gallery';
import Contact from '../components/Contact';
import Footer from '../components/Footer';
import FloatingCTA from '../components/FloatingCTA';
import { Toaster } from "@/components/ui/toaster";
import { initializeTracking } from '../utils/analytics';

const Index = () => {
  // Initialize intersection observer for reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-slide').forEach((el) => {
      observer.observe(el);
    });

    // Initialize analytics tracking
    initializeTracking();

    return () => {
      document.querySelectorAll('.reveal-slide').forEach((el) => {
        observer.unobserve(el);
      });
    };
  }, []);
  
  // Set up document title and meta description for SEO
  useEffect(() => {
    document.title = 'Denver Plumber with Flat-Rate Pricing | Colorado First Plumbing';
    
    const metaTags = [
      { name: 'description', content: 'Get honest, upfront plumbing prices in Denver Metro for water heaters, sewer repair & more. Licensed Master Plumbers. 360 Google Reviews 4.8★ rating. No hidden fees ever.' },
      { name: 'keywords', content: 'Denver plumber flat-rate pricing, upfront plumbing costs Denver, transparent pricing plumber, no hidden fees plumber Denver, real prices online plumber, honest plumber Denver Metro, exact plumbing costs' },
      { property: 'og:title', content: 'Denver Plumber with Flat-Rate Pricing | Colorado First Plumbing' },
      { property: 'og:description', content: 'Get honest, upfront plumbing prices in Denver Metro for water heaters, sewer repair & more. Licensed Master Plumbers. 360 Google Reviews 4.8★ rating. No hidden fees ever.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://colorado-plumbing.com/' },
      { property: 'og:image', content: 'https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Prices-you-can-see-Reviews-you-can-trust.png' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Denver Plumber with Flat-Rate Pricing | Colorado First Plumbing' },
      { name: 'twitter:description', content: 'Get honest, upfront plumbing prices in Denver Metro for water heaters, sewer repair & more. Licensed Master Plumbers. 360 Google Reviews 4.8★ rating. No hidden fees ever.' },
      { name: 'twitter:image', content: 'https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Prices-you-can-see-Reviews-you-can-trust.png' }
    ];
    
    metaTags.forEach(tag => {
      let metaElement = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (metaElement) {
        if (tag.name) {
          metaElement.setAttribute('name', tag.name);
        } else {
          metaElement.setAttribute('property', tag.property);
        }
        metaElement.setAttribute('content', tag.content);
      } else {
        metaElement = document.createElement('meta');
        if (tag.name) {
          metaElement.setAttribute('name', tag.name);
        } else {
          metaElement.setAttribute('property', tag.property);
        }
        metaElement.setAttribute('content', tag.content);
        document.head.appendChild(metaElement);
      }
    });
    
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', 'https://colorado-plumbing.com/');
      document.head.appendChild(canonicalLink);
    }
    
    // Add FAQ Schema for actual homepage questions
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Where can I find pricing for Denver water heaters provided and installed?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can find our exact, flat-rate Denver water heater prices directly on our Water Heater Price Page. Colorado First Plumbing and Sewer LLC is Denver's only licensed contractor to openly display \"out the door\" pricing online, including a 40-gallon atmospheric water heater for $2,000. This page details costs for Powervented, Standard, Power Direct Vent, and Atmospheric Water Heaters."
          }
        },
        {
          "@type": "Question",
          "name": "Do you provide exact cost for denver tankless water heater installations and tankless water heater replacements?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, our Tankless Water Heater Pricing page provides out the door pricing for both Rinnai RE180IN and Navien NPE-240A2 tankless water heater new installs and existing replacements. This upfront price on tankless water heaters is available to Denver, Lakewood, Littleton, Highlands Ranch, Centennial, Arvada, Thornton, Castle Rock and Longmont."
          }
        },
        {
          "@type": "Question",
          "name": "Do you provide Flat Rate, Upfront pricing for Sewer Repairs and Replacements?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Colorado First Plumbing provides clear costs and pricing for many sewer repairs and replacement in Denver, Littleton, lakewood, Highlands Ranch and Surrounding areas. From chipping up the basement, to excavating in the street, we provide clear prices for any Denver Sewer Main Replacement. What we include and dont include are clearly posted, which is one of the reasons that we are the one of the most trusted when it comes to expert sewer repairs."
          }
        },
        {
          "@type": "Question",
          "name": "How do you know if a plumbing company is truly reputable in Denver?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We hold a 4.8 average with 360 Google Reviews, 92 HomeAdvisor Reviews and over 300 Birdeye Reviews. Buildzoom list our company as the Top 2% of Plumbers in Littleton, Denver and Surrounding areas. These sources are trusted, verifiable, sources."
          }
        },
        {
          "@type": "Question",
          "name": "Are these Flat Rate Water Heater Prices only available in Denver?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No, our transparent flat-rate water heater pricing is available throughout the Denver Metro area including Littleton, Highlands Ranch, Lakewood, Arvada, Thornton, Centennial, Longmont, Castle Rock, and Aurora. We apply our transparent pricing model consistently across all these service areas."
          }
        },
        {
          "@type": "Question",
          "name": "",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes We use name-brand, industry-proven items such as Bradford White, Rheem, Rinnai, Navien, Delta, Moen, and Glacier Bay. , which is clearly marked on our specific service price pages."
          }
        },
        {
          "@type": "Question",
          "name": "What other trusted Denver Plumbers show exact upfront pricing?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Colorado First Plumbing and Sewer LLC is the ONLY licensed plumbing contractor in Colorado showing exact upfront pricing online. While many plumbers claim to offer \"transparent\" or \"honest\" pricing, we're the only ones who actually post real prices before you call. This transparency has earned us over 360 Google Reviews with a 4.8-star average."
          }
        },
        {
          "@type": "Question",
          "name": "Why choose Navien vs Rinnai?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Both are excellent choices. Colorado First Plumbing is factory-trained on both brands. Navien features built-in recirculation and buffer tank. Rinnai offers industry-leading reliability."
          }
        }
      ]
    };
    
    // Inject FAQ schema into the document head
    let faqSchemaElement = document.querySelector('script[data-faq-schema]') as HTMLScriptElement;
    if (!faqSchemaElement) {
      faqSchemaElement = document.createElement('script') as HTMLScriptElement;
      faqSchemaElement.type = 'application/ld+json';
      faqSchemaElement.setAttribute('data-faq-schema', 'true');
      faqSchemaElement.innerHTML = JSON.stringify(faqSchema, null, 2);
      document.head.appendChild(faqSchemaElement);
    }
    
    // Complete Schema with @graph - combines all schemas into one unified structure
    const completeSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://colorado-plumbing.com/#organization",
          "name": "Colorado First Plumbing and Sewer LLC",
          "url": "https://colorado-plumbing.com/",
          "logo": "https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Colorado-First-Plumbing-Logo-Transparent.png",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "5108 Sydney Ave",
            "addressLocality": "Littleton",
            "addressRegion": "CO",
            "postalCode": "80130",
            "addressCountry": "US"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer support"
          },
          "sameAs": [
            "https://www.facebook.com/coloradoplumbing/",
            "https://g.co/kgs/xd3PznG",
            "https://www.tiktok.com/@milehighplumber",
            "https://www.youtube.com/@bestmasterplumber",
            "https://www.instagram.com/coloradofirstplumbing/",
            "https://birdeye.com/colorado-first-plumbing-and-sewer-llc-172384402926614",
            "https://www.bbb.org/us/co/highlands-ranch/profile/plumber/colorado-first-plumbing-and-sewer-llc-1296-90268915",
            "https://www.buildzoom.com/contractor/colorado-first-plumbing-and-sewer-inc"
          ],
          "description": "Licensed Master Plumber in the Denver Metro area offering transparent, flat-rate pricing for residential and commercial plumbing and sewer services.",
          "founder": [
            {
              "@type": "Person",
              "name": "Codee Jessen",
              "jobTitle": "Licensed Master Plumber"
            },
            {
              "@type": "Person", 
              "name": "Michelle Jessen",
              "jobTitle": "Majority Owner"
            }
          ]
        },
        {
          "@type": "Plumber",
          "@id": "https://colorado-plumbing.com/",
          "name": "Colorado First Plumbing and Sewer LLC",
          "image": "https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Colorado-First-Plumbing-Logo-Transparent.png",
          "logo": "https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Colorado-First-Plumbing-Logo-Transparent.png",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "5108 Sydney Ave",
            "addressLocality": "Littleton",
            "addressRegion": "CO",
            "postalCode": "80130",
            "addressCountry": "US"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 39.53934,
            "longitude": -104.92936
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
              "opens": "08:00",
              "closes": "17:00"
            }
          ],
          "url": "https://colorado-plumbing.com/",
          "description": "Licensed Master Plumber in the Denver Metro area offering transparent, flat-rate pricing for residential and commercial plumbing and sewer services.",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "360"
          },
          "areaServed": ["Denver", "Highlands Ranch", "Littleton", "Castle Rock", "Longmont", "Centennial", "Thornton", "Arvada", "Lakewood"],
          "hasMap": "https://maps.app.goo.gl/T8cAeQbDFS4hP7qQ8",
          "publicAccess": false,
          "sameAs": [
            "https://www.facebook.com/coloradoplumbing/",
            "https://g.co/kgs/xd3PznG",
            "https://www.tiktok.com/@milehighplumber",
            "https://www.youtube.com/@bestmasterplumber",
            "https://www.instagram.com/coloradofirstplumbing/",
            "https://birdeye.com/colorado-first-plumbing-and-sewer-llc-172384402926614",
            "https://www.bbb.org/us/co/highlands-ranch/profile/plumber/colorado-first-plumbing-and-sewer-llc-1296-90268915",
            "https://www.buildzoom.com/contractor/colorado-first-plumbing-and-sewer-inc"
          ]
        },
        {
          "@type": "WebSite",
          "@id": "https://colorado-plumbing.com/#website", 
          "url": "https://colorado-plumbing.com/",
          "name": "Colorado First Plumbing and Sewer LLC",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://colorado-plumbing.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        },
        {
          "@type": "VideoObject",
          "@id": "https://colorado-plumbing.com/#video1",
          "name": "Why We Publish Real Plumbing Prices - Colorado First Plumbing",
          "description": "Learn why Colorado First Plumbing is the only plumber in Colorado showing exact prices online with no hidden fees.",
          "thumbnailUrl": "https://i.ytimg.com/vi/dOxhZowN8To/maxresdefault.jpg",
          "uploadDate": "2024-01-01T00:00:00-07:00",
          "contentUrl": "https://www.youtube.com/watch?v=dOxhZowN8To",
          "embedUrl": "https://www.youtube.com/embed/dOxhZowN8To"
        },
        {
          "@type": "VideoObject",
          "@id": "https://colorado-plumbing.com/#video2",
          "name": "Colorado First Plumbing Services",
          "description": "Professional plumbing services in Denver Metro with transparent pricing and master plumber expertise.",
          "thumbnailUrl": "https://i.ytimg.com/vi/WzQdOtVNeUU/maxresdefault.jpg",
          "uploadDate": "2024-01-01T00:00:00-07:00",
          "contentUrl": "https://www.youtube.com/watch?v=WzQdOtVNeUU",
          "embedUrl": "https://www.youtube.com/embed/WzQdOtVNeUU"
        }
      ]
    };

    // Inject complete schema into the document head
    let completeSchemaElement = document.querySelector('script[data-complete-schema]') as HTMLScriptElement;
    if (!completeSchemaElement) {
      completeSchemaElement = document.createElement('script') as HTMLScriptElement;
      completeSchemaElement.type = 'application/ld+json';
      completeSchemaElement.setAttribute('data-complete-schema', 'true');
      completeSchemaElement.innerHTML = JSON.stringify(completeSchema, null, 2);
      document.head.appendChild(completeSchemaElement);
    }
  }, []);
  
  // Handle scrolling behavior - smooth scroll to anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A') {
        const href = target.getAttribute('href');
        if (href && href.startsWith('#')) {
          e.preventDefault();
          const element = document.querySelector(href);
          if (element) {
            window.scrollTo({
              top: element.getBoundingClientRect().top + window.scrollY - 80,
              behavior: 'smooth'
            });
          }
        }
      }
    };
    
    document.addEventListener('click', handleAnchorClick);
    
    return () => {
      document.removeEventListener('click', handleAnchorClick);
    };
  }, []);
  
  // HCP widget is now loaded by Hero component

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-950">
      <Header />
      <main>
        <Hero />
        
        {/* Trust & Authority Section - Immediately after hero */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Why Colorado Trusts Our Transparent Pricing
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
                <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
                <p className="text-blue-100 text-sm">Since 2018 serving Denver Metro, Highlands Ranch, Littleton, and surrounding areas</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">360</div>
                <div className="text-lg font-semibold text-white mb-2">Google Reviews</div>
                <p className="text-blue-100 text-sm">4.8★ rating + 300+ BirdEye Reviews, 92+ HomeAdvisor Reviews</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
                <div className="text-lg font-semibold text-white mb-2">CO Jurisdictions</div>
                <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities including Denver Metro, with all permits and inspections handled</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
                <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
                <p className="text-blue-100 text-sm">Accredited 7/15/2024</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
                <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
                <p className="text-blue-100 text-sm">Showing Affordable Prices Online with 360 Google Reviews 4.8★ Rating</p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Our Prices Are Always Upfront - Instant Differentiation */}
        <section className="py-16 bg-gradient-to-br from-blue-900 to-blue-950 overflow-hidden relative">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Why We Publish Real Plumbing Prices
              </h2>
              
              {/* Video Embed */}
              <div className="mb-8 flex justify-center">
                <div className="relative w-full max-w-2xl">
                  <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    <iframe 
                      className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
                      src="https://www.youtube.com/embed/dOxhZowN8To?si=9a5YNP2DIhE-CmLJ"
                      title="Why We Publish Real Plumbing Prices - Colorado First Plumbing"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
                <p className="text-xl mb-6 text-white font-semibold">
                  We live in a world where the average homeowner is bombarded daily by sales calls, scam calls, and pushy salesmen. Unfortunately, the plumbing industry is no different.
                </p>
                
                <p className="text-lg mb-6 text-blue-100 leading-relaxed">
                  Most plumbing companies refuse to publish real prices for high-ticket services like Water Heater Replacement Cost, Sewer Repair and Replacement Pricing, and Tankless Water Heater Installation Cost—even for smaller items such as how much a garbage disposal replacement should cost.
                </p>

                <p className="text-lg mb-6 text-blue-100">
                  This isn't an oversight. <strong className="text-yellow-400">It's by design.</strong> Big Box Plumbing companies often send trained salespeople instead of true plumbers. Their job is to adjust the price on the spot—based on how desperate you seem during your emergency, the neighborhood you live in, and any other factor that increases their profit.
                </p>

                <p className="text-lg mb-6 text-blue-100">
                  We refuse to play that game. At Colorado First Plumbing & Sewer, we don't believe in hiding behind vague promises of "affordable upfront pricing." We choose people over profit by giving you the information you need to make an informed decision before the plumber arrives.
                </p>

                <p className="text-lg mb-6 text-blue-100">
                  Search online for "Plumber Littleton," "Water Heater Price Highlands Ranch," or "Sewer Repair Denver Metro" and you'll see the same marketing tricks everywhere:
                </p>

                <ul className="text-lg mb-6 text-blue-100 space-y-2 ml-6">
                  <li>• "Affordable Priced Plumber" with no actual numbers.</li>
                  <li>• "Upfront Pricing" that only shows up once a salesperson is standing in your kitchen.</li>
                  <li>• Misleading ranges like "Water Heater near me: $1,000–$100,000."</li>
                </ul>

                <p className="text-lg mb-6 text-blue-100">
                  <strong className="text-yellow-400">We are different.</strong> We're Colorado's ONLY plumber with exact prices online—so you can see the real cost of a water heater, tankless system, or sewer replacement before you ever call us. Every photo you see on this site is of our actual work—no stock photos, no gimmicks.
                </p>

                <p className="text-lg mb-8 text-blue-100">
                  As a family-owned business led by Licensed Master Plumber Codee Jessen, we've proudly served the Denver Metro since 2018 with complete honesty and transparency. Our <strong className="text-yellow-400">360 Google Reviews (4.8★ average)</strong> prove one thing: when you treat people fairly and show them the real cost upfront, you earn trust for life.
                </p>

                {/* City-Specific Pricing Examples */}
                <div className="bg-white/5 rounded-xl p-6 mb-8 border border-white/10">
                  <h4 className="font-bold text-yellow-300 mb-4 text-lg">See Exact Prices for Your City:</h4>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-blue-100">
                    <li>✅ <strong>See Actual Water Heater Pricing in Littleton</strong> - $2,000 for 40 gallon</li>
                    <li>✅ <strong>See Upfront Sewer Repair Cost in Lakewood</strong> - $5,000 spot repair</li>
                    <li>✅ <strong>See Affordable Tankless Water Heater Pricing for Thornton</strong> - $5,000 replacement</li>
                    <li>✅ <strong>See Exact Drain Cleaning Cost in Denver</strong> - $295-$695</li>
                    <li>✅ <strong>See Real Water Heater Prices in Highlands Ranch</strong> - $2,100 for 50 gallon</li>
                    <li>✅ <strong>See Transparent Sewer Scope Pricing in Arvada</strong> - $175 camera inspection</li>
                    <li>✅ <strong>See Honest Plumbing Costs in Castle Rock</strong> - No hidden fees ever</li>
                    <li>✅ <strong>See Upfront Tankless Installation in Centennial</strong> - $8,000 new install</li>
                  </ul>
                </div>

                {/* Key Differentiators */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="text-2xl mb-2">💰</div>
                    <h3 className="font-bold text-white mb-2">Real Prices Online</h3>
                    <p className="text-sm text-blue-200">No estimates, no ranges - see exact costs before we arrive</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="text-2xl mb-2">📸</div>
                    <h3 className="font-bold text-white mb-2">Authentic Work Photos</h3>
                    <p className="text-sm text-blue-200">Every image shows our actual projects and results</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="text-2xl mb-2">🚫</div>
                    <h3 className="font-bold text-white mb-2">No Sales Games</h3>
                    <p className="text-sm text-blue-200">No salespeople, no upsells, no hidden fees</p>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="/pricing" 
                    className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-gray-900 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02]"
                  >
                    💰 See All Real Prices
                  </a>
                  <a 
                    href="/water-heater-pricing" 
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02]"
                  >
                    🔥 Water Heater: $2,000
                  </a>
                </div>

                <p className="text-sm text-blue-200 mt-4 italic">
                  The price you see is the price you pay. No exceptions.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Comprehensive Services Overview - Moved below transparency section */}
        <section className="py-16 bg-gradient-to-br from-blue-900 to-blue-950 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                Complete Plumbing Services for Colorado
              </h2>
              <p className="text-xl md:text-2xl font-semibold text-blue-200 max-w-3xl mx-auto leading-relaxed">
                From residential repairs to commercial installations, we handle every plumbing need with 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-400"> transparent pricing</span> and 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400"> master plumber expertise</span>.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Water Heater Replacement - PRIORITY #1 */}
              <div className="relative rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-yellow-300 overflow-hidden group">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/colorado-plumbing-uploads/services/service-projects/50-gallon-state-water-heater-replacement-highlands-ranch.JPEG)',
                    filter: 'brightness(0.7)'
                  }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/50 to-blue-800/50" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl">🔥</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Water Heater Replacement</h3>
                  <p className="text-yellow-100 mb-4">Professional water heater installation with exact online pricing - no hidden fees or surprises.</p>
                  <div className="flex flex-col gap-2">
                    <a 
                      href="/water-heater-pricing" 
                      className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-blue-900 px-4 py-2 rounded-full font-bold text-sm transition-all duration-300 hover:shadow-lg text-center"
                    >
                      💰 See Water Heater Pricing
                    </a>
                    <a 
                      href="/flat-rate-affordable-rheem-water-heaters-in-colorado" 
                      className="bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white px-4 py-2 rounded-full font-bold text-sm transition-all duration-300 hover:shadow-lg text-center"
                    >
                      🔥 Special Deal $2,000
                    </a>
                  </div>
                </div>
              </div>

              {/* Tankless Water Heater Installation - PRIORITY #2 */}
              <div className="relative rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-orange-300 overflow-hidden group">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/colorado-plumbing-uploads/locations/longmont/tankless-install-navien-colorado-first-plumbing-brighton.JPEG)',
                    filter: 'brightness(0.7)'
                  }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/50 to-blue-800/50" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl">♨️</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Tankless Water Heater Installation</h3>
                  <p className="text-orange-100 mb-4">Energy-efficient Navien & Rinnai tankless systems with transparent pricing and rebate assistance.</p>
                  <a 
                    href="/tankless-water-heater-pricing" 
                    className="bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-4 py-2 rounded-full font-bold text-sm transition-all duration-300 hover:shadow-lg text-center inline-block w-full"
                  >
                    💵 See Tankless Pricing
                  </a>
                </div>
              </div>

              {/* Sewer Repair & Replacement - PRIORITY #3 */}
              <div className="relative rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-blue-300 overflow-hidden group">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/colorado-plumbing-uploads/existing-projects/sewer-repair-experts.png)',
                    filter: 'brightness(0.7)'
                  }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-700/50 to-blue-900/50" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl">🚰</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Sewer Repair & Replacement</h3>
                                    <a 
                    href="/sewer-replacement-spot-repair-pricing" 
                    className="bg-gradient-to-r from-cyan-400 to-cyan-500 hover:from-cyan-500 hover:to-cyan-600 text-white px-4 py-2 rounded-full font-bold text-sm transition-all duration-300 hover:shadow-lg text-center inline-block w-full"
                  >
                    💰 See Repair Pricing
                  </a>
                </div>
              </div>

              {/* Sewer Drain Cleaning - PRIORITY #4 */}
              <div className="relative rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-green-300 overflow-hidden group">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/page-specific-content/littleton-plumber/sewer-snake-machine-spartan-300-clear-main-littleton.jpg)',
                    filter: 'brightness(0.7)'
                  }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/50 to-blue-800/50" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl">🚿</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Sewer Drain Cleaning</h3>
                  <p className="text-green-100 mb-4">Professional drain cleaning, root removal, and hydro jetting services with upfront pricing.</p>
                  <a 
                    href="/sewer-cleaning-pricing" 
                    className="bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white px-4 py-2 rounded-full font-bold text-sm transition-all duration-300 hover:shadow-lg text-center inline-block w-full"
                  >
                    🚿 See Cleaning Prices
                  </a>
                </div>
              </div>
              
              {/* Residential Plumbing */}
              <div className="relative rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-indigo-300 overflow-hidden group">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/colorado-plumbing-uploads/existing-projects/plumbing-truck-residential-best-plumber-castle-rock.png)',
                    filter: 'brightness(0.7)'
                  }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/50 to-blue-800/50" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl">🏠</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Residential Plumbing</h3>
                  <p className="text-indigo-100 mb-4">Complete home plumbing services including fixtures, pipes, and emergency repairs.</p>
                  <a 
                    href="/residential-plumbing" 
                    className="bg-gradient-to-r from-indigo-400 to-indigo-500 hover:from-indigo-500 hover:to-indigo-600 text-white px-4 py-2 rounded-full font-bold text-sm transition-all duration-300 hover:shadow-lg text-center inline-block w-full"
                  >
                    🏠 Explore Residential Services
                  </a>
                </div>
              </div>
              
              {/* Commercial Plumbing */}
              <div className="relative rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-purple-300 overflow-hidden group">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/colorado-plumbing-uploads/services/commercial-plumbing/commercial-hanging-pipe-multifamily-residential.jpg)',
                    filter: 'brightness(0.7)'
                  }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/50 to-blue-800/50" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl">🏢</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Commercial Plumbing</h3>
                  <p className="text-purple-100 mb-4">Professional plumbing solutions for businesses with minimal disruption and code compliance.</p>
                  <a 
                    href="/commercial-plumbing" 
                    className="bg-gradient-to-r from-purple-400 to-purple-500 hover:from-purple-500 hover:to-purple-600 text-white px-4 py-2 rounded-full font-bold text-sm transition-all duration-300 hover:shadow-lg text-center inline-block w-full"
                  >
                    🏢 View Commercial Solutions
                  </a>
                </div>
              </div>
              
              {/* Bathroom & Kitchen Remodel */}
              <div className="relative rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-pink-300 overflow-hidden group">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/colorado-plumbing-uploads/services/remodels/littleton-finish-bathroom.JPEG)',
                    filter: 'brightness(0.7)'
                  }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-800/50 to-blue-950/50" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl">🛁</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Bathroom & Kitchen Remodel</h3>
                  <p className="text-pink-100 mb-4">Complete renovation plumbing services for bathroom and kitchen remodeling projects.</p>
                  <a 
                    href="/bathroom-kitchen-remodel" 
                    className="bg-gradient-to-r from-pink-400 to-pink-500 hover:from-pink-500 hover:to-pink-600 text-white px-4 py-2 rounded-full font-bold text-sm transition-all duration-300 hover:shadow-lg text-center inline-block w-full"
                  >
                    🛁 See Remodeling Options
                  </a>
                </div>
              </div>
              
              {/* Custom Home Plumbing */}
              <div className="relative rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-red-300 overflow-hidden group">
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-105"
                  style={{
                    backgroundImage: 'url(/colorado-plumbing-uploads/services/remodels/Custom-Home-Denver-Front-view.jpg)',
                    filter: 'brightness(0.7)'
                  }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 to-blue-950/50" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl">🏗️</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">Custom Home Plumbing</h3>
                  <p className="text-red-100 mb-4">Specialized plumbing design and installation for new construction and custom home projects.</p>
                  <a 
                    href="/custom-home-plumbing" 
                    className="bg-gradient-to-r from-red-400 to-red-500 hover:from-red-500 hover:to-red-600 text-white px-4 py-2 rounded-full font-bold text-sm transition-all duration-300 hover:shadow-lg text-center inline-block w-full"
                  >
                    🏗️ Explore Custom Plumbing
                  </a>
                </div>
              </div>
              
            </div>
            
            {/* Ready for Service Card */}
            <div className="mt-12">
              <ReadyForServiceCard />
            </div>
          </div>
        </section>
        
        {/* Social Proof Bar - Immediately establishes credibility after hero */}
        <SocialProofBar />

        {/* Water Heater Replacements Q&A Section */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto mb-12">
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 font-bold text-sm border border-yellow-200 shadow-lg">
                💰 EXACT WATER HEATER PRICING
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Questions and Answers Other Plumbing Companies Can't or Won't Answer
              </h2>
              <h3 className="text-2xl md:text-3xl font-semibold text-yellow-300 mb-4">
                Water Heater Replacements
              </h3>
              <p className="text-lg text-blue-100">
                See exact prices before we arrive - no estimates, no ranges, no surprises
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Content Side */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    
                    {/* Question 1 */}
                    <div className="mb-8 pb-6 border-b border-white/20">
                      <h4 className="text-xl font-bold text-white mb-3">📍 Where can I find pricing for Denver water heaters provided and installed?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        You can find our exact, flat-rate Denver water heater prices directly on our{' '}
                        <a href="/water-heater-pricing" className="text-yellow-300 hover:text-yellow-200 underline font-semibold">
                          Water Heater Price Page
                        </a>. Colorado First Plumbing and Sewer LLC is Denver's only licensed contractor to openly display "out the door" pricing online, including a 40-gallon atmospheric water heater for $2,000. This page details costs for Powervented, Standard, Power Direct Vent, and Atmospheric Water Heaters.
                      </p>
                      <div className="text-center">
                        <a 
                          href="/water-heater-pricing"
                          className="bg-gradient-to-r from-yellow-400 to-orange-400 hover:shadow-xl hover:scale-[1.02] text-blue-900 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 inline-block"
                        >
                          💰 See Upfront Denver Water Heater Pricing
                        </a>
                        <p className="text-sm text-blue-200 mt-2">
                          Flat Rate Prices for Standard, Atmospheric, Powervent and other types of Tank Water Heaters.
                        </p>
                      </div>
                    </div>

                    {/* Question 2 */}
                    <div className="mb-8">
                      <h4 className="text-xl font-bold text-white mb-3">🏠 Are these Flat Rate Water Heater Prices only available in Denver?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        No, these flat-rate prices are available to anyone in the entire{' '}
                        <a href="/denver-plumber" className="text-yellow-300 hover:text-yellow-200 underline font-semibold">Denver</a>{' '}
                        Metro Area. This includes locations like{' '}
                        <a href="/littleton-plumber" className="text-yellow-300 hover:text-yellow-200 underline font-semibold">Littleton</a>,{' '}
                        <a href="/highlands-ranch-plumber" className="text-yellow-300 hover:text-yellow-200 underline font-semibold">Highlands Ranch</a>,{' '}
                        <a href="/lakewood-plumber" className="text-yellow-300 hover:text-yellow-200 underline font-semibold">Lakewood</a>,{' '}
                        <a href="/arvada-plumber" className="text-yellow-300 hover:text-yellow-200 underline font-semibold">Arvada</a>,{' '}
                        <a href="/thornton-plumber" className="text-yellow-300 hover:text-yellow-200 underline font-semibold">Thornton</a>,{' '}
                        <a href="/centennial-plumber" className="text-yellow-300 hover:text-yellow-200 underline font-semibold">Centennial</a>,{' '}
                        <a href="/longmont-plumber" className="text-yellow-300 hover:text-yellow-200 underline font-semibold">Longmont</a>,{' '}
                        <a href="/castle-rock-plumber" className="text-yellow-300 hover:text-yellow-200 underline font-semibold">Castle Rock</a>, and Aurora. We apply our transparent pricing model consistently across all these service areas.
                      </p>
                      <div className="text-center">
                        <a 
                          href="/flat-rate-affordable-rheem-water-heaters-in-colorado"
                          className="bg-gradient-to-r from-yellow-400 to-orange-400 hover:shadow-xl hover:scale-[1.02] text-blue-900 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 inline-block"
                        >
                          🔥 See affordable Rheem water heaters for Denver
                        </a>
                        <p className="text-sm text-blue-200 mt-2">
                          Most affordable, all inclusive price for Standard 40 and 50 gallon atmospheric rheem hot water heaters.
                        </p>
                      </div>
                    </div>

                    {/* Question 3 - Expert Recognition */}
                    <div className="mb-8 pb-6 border-b border-white/20">
                      <h4 className="text-xl font-bold text-white mb-3">🏆 Is Colorado First Plumbing and Sewer LLC recognized for expert water heater installations in the Denver Metro Area?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        Yes, Colorado First Plumbing is highly recognized as a expert water heater installer in the Denver Metro Area. Codee Jessen, a Colorado Licensed Master Plumber with over 20,000 hours in the plumbing trade, has built this company on the foundation of expertise, and transparency.
                      </p>
                    </div>

                    {/* Question 4 - Quality Materials */}
                    <div className="mb-8 pb-6 border-b border-white/20">
                      <h4 className="text-xl font-bold text-white mb-3">🔧 What Materials do quality Denver Plumbers use with water heater installs?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        Colorado First Plumbing uses high-quality, industry-proven materials like Viega Propress, brass, copper, approved transitional couplings, and black iron for gas lines, sourced from reputable plumbing supply stores such as Hughes Supply and Ferguson.
                      </p>
                    </div>

                    {/* Question 5 - Reputation Verification */}
                    <div className="mb-8 pb-6 border-b border-white/20">
                      <h4 className="text-xl font-bold text-white mb-3">⭐ How do you know if a plumbing company is truly reputable in Denver?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        We hold a 4.8 average with 360 Google Reviews, 92 HomeAdvisor Reviews and over 300 Birdeye Reviews. Buildzoom list our company as the Top 2% of Plumbers in Littleton, Denver and Surrounding areas. These sources are trusted, verifiable, sources.
                      </p>
                    </div>

                    {/* Question 6 - Experience Demonstration */}
                    <div className="mb-8">
                      <h4 className="text-xl font-bold text-white mb-3">📸 How do you know if a Denver Plumbing company is experienced in replacing water heaters?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        We demonstrate our expertise by providing over 3,000 photos and videos of our expert, inspection-ready water heater installations, sewer projects and renovations across Denver Metro through all forms of Social Media.
                      </p>
                    </div>

                    {/* Call to Action Buttons */}
                    <div className="text-center space-y-4">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300"
                        >
                          📝 Submit Water Heater Form
                        </button>
                        
                      </div>
                    </div>
                  </div>
                </div>

                {/* Images Side */}
                <div className="space-y-6">
                  <img
                    src="/colorado-plumbing-uploads/locations/aurora/rheem-40-gallon-standard-replaced-aurora.JPEG"
                    alt="Rheem 40 gallon atmospheric water heater replacement in Aurora for $2,000"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                    loading="lazy"
                  />
                  <img
                    src="/page-specific-content/water-heater-pricing/affordable-rheem-40-gallon-atmospheric.JPEG"
                    alt="Affordable Rheem 50 gallon water heater installation for $2100"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                    loading="lazy"
                  />
                  <img
                    src="/colorado-plumbing-uploads/locations/arvada/bradford-white-50-gallon-replace-arvada.JPEG"
                    alt="Bradford White 50 gallon replacement in Arvada for $2,400"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tankless Water Heater Installation Q&A Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-800 to-blue-900 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto mb-12">
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 font-bold text-sm border border-orange-200 shadow-lg">
                🔥 EXACT TANKLESS PRICING
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Questions and Answers Other Plumbing Companies Can't or Won't Answer
              </h2>
              <h3 className="text-2xl md:text-3xl font-semibold text-yellow-300 mb-4">
                Tankless Water Heater Installation
              </h3>
              <p className="text-lg text-blue-100">
                See exact, affordable prices for Navien & Rinnai tankless systems - no hidden fees
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Images Side First */}
                <div className="space-y-6 order-2 lg:order-1">
                  <img
                    src="/colorado-plumbing-uploads/locations/centennial/tankless-water-heater-professional-replacement-centennial.jpeg"
                    alt="Professional tankless water heater replacement in Centennial"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                    loading="lazy"
                  />
                  <img
                    src="/colorado-plumbing-uploads/locations/longmont/tankless-install-navien-colorado-first-plumbing-brighton.JPEG"
                    alt="Navien tankless water heater professional installation"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                    loading="lazy"
                  />
                  <img
                    src="/colorado-plumbing-uploads/locations/denver/expert-tankless-rinnai-install-denver.JPEG"
                    alt="Expert Rinnai tankless installation in Denver"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                    loading="lazy"
                  />
                </div>

                {/* Content Side */}
                <div className="order-1 lg:order-2">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    
                    {/* Question 1 - New Tankless Question */}
                    <div className="mb-8 pb-6 border-b border-white/20">
                      <h4 className="text-xl font-bold text-white mb-3">🔥 Do you provide exact cost for denver tankless water heater installations and tankless water heater replacements?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        Yes, our Tankless Water Heater Pricing page provides out the door pricing for both Rinnai RE180IN and Navien NPE-240A2 tankless water heater new installs and existing replacements. This upfront price on tankless water heaters is available to Denver, Lakewood, Littleton, Highlands Ranch, Centennial, Arvada, Thornton, Castle Rock and Longmont.
                      </p>
                      <div className="text-center">
                        <a 
                          href="/tankless-water-heater-pricing"
                          className="bg-gradient-to-r from-yellow-400 to-orange-400 hover:shadow-xl hover:scale-[1.02] text-blue-900 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 inline-block"
                        >
                          🔥 See our trusted Denver Tankless install pricing
                        </a>
                        <p className="text-sm text-blue-200 mt-2">
                          Our Upfront and Transparent Denver Tankless water heater cost.
                        </p>
                      </div>
                    </div>

                    {/* Question 2 */}
                    <div className="mb-8">
                      <h4 className="text-xl font-bold text-white mb-3">🔥 Why choose Navien vs Rinnai?</h4>
                      <div className="text-2xl font-bold text-yellow-300 mb-2">Both Excellent Choices</div>
                      <p className="text-blue-100 mb-3">Colorado First Plumbing is factory-trained on both brands</p>
                      <ul className="text-sm text-blue-200 space-y-1">
                        <li>✅ Navien: Built-in recirculation, buffer tank</li>
                        <li>✅ Rinnai: Industry-leading reliability</li>
                                                <li>✅ Both: Cold climate freeze protection</li>
                      </ul>
                    </div>

                    {/* Question 3 - Denver Plumber Tankless Knowledge */}
                    <div className="mb-8 pb-6 border-b border-white/20">
                      <h4 className="text-xl font-bold text-white mb-3">🔧 What does a Denver Plumber need to know when it comes to Tankless Water Heaters?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        Tankless water heaters should only be installed by qualified, experienced, and Licensed Plumbing Contractors. It's not just about connecting pipes; it requires specialized knowledge of internal systems (condensation, recirculation), external factors (venting, gas lines, elevation, water temperature), and the skill of a expert who has training and experience with these systems to ensure the machine runs flawlessly and safely for years.
                      </p>
                    </div>

                    {/* Question 4 - Why Choose Colorado First */}
                    <div className="mb-8 pb-6 border-b border-white/20">
                      <h4 className="text-xl font-bold text-white mb-3">🏆 Why is Colorado First Plumbing the right choice to make when choosing the right denver tankless water heater installer?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        We specialize in Navien and Rinnai Tankless Water Heater Replacements, Repairs and Installs. We provide transparent costs for expert tankless water heater installations in Denver and Surrounding areas like Littleton. We understand the technical intricacies and regulatory requirements of tankless water heaters, the importance of using quality materials, and the value of clear, upfront pricing.
                      </p>
                    </div>

                    {/* Question 5 - Competitive Transparency */}
                    <div className="mb-8">
                      <h4 className="text-xl font-bold text-white mb-3">💰 What other trusted Denver Plumbers show exact upfront pricing?</h4>
                                          </div>

                    {/* Call to Action Buttons */}
                    <div className="text-center space-y-4">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300"
                        >
                          📝 Submit Tankless Form
                        </button>
                        
                      </div>
                      <a 
                        href="/tankless-water-heater-pricing"
                        className="bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 inline-block"
                      >
                        🔥 See Full Tankless Pricing
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sewer Repair and Replacement Q&A Section */}
        <section className="py-16 bg-gradient-to-br from-blue-900 to-blue-950 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto mb-12">
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 font-bold text-sm border border-blue-200 shadow-lg">
                🚰 EXACT SEWER PRICING
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Questions and Answers Other Plumbing Companies Can't or Won't Answer
              </h2>
              <h3 className="text-2xl md:text-3xl font-semibold text-yellow-300 mb-4">
                Sewer Repair and Replacement
              </h3>
              <p className="text-lg text-blue-100">
                See exact, affordable prices for sewer spot repair and full replacement - no hidden fees
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Content Side */}
                <div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                    
                    {/* Question 1 - Flat Rate Pricing */}
                    <div className="mb-8 pb-6 border-b border-white/20">
                      <h4 className="text-xl font-bold text-white mb-3">🚰 Do you provide Flat Rate, Upfront pricing for Sewer Repairs and Replacements?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        Colorado First Plumbing provides clear costs and pricing for many sewer repairs and replacement in Denver, Littleton, lakewood, Highlands Ranch and Surrounding areas. From chipping up the basement, to excavating in the street, we provide clear prices for any Denver Sewer Main Replacement. What we include and dont include are clearly posted, which is one of the reasons that we are the one of the most trusted when it comes to expert sewer repairs.
                      </p>
                      <div className="text-center">
                        <a 
                          href="/sewer-replacement-spot-repair-pricing"
                          className="bg-gradient-to-r from-yellow-400 to-orange-400 hover:shadow-xl hover:scale-[1.02] text-blue-900 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 inline-block"
                        >
                          🚰 See Full Sewer Pricing
                        </a>
                        <p className="text-sm text-blue-200 mt-2">
                          Clear, upfront prices for all sewer repair and replacement services.
                        </p>
                      </div>
                    </div>

                    {/* Question 2 - Transparency Comparison */}
                    <div className="mb-8 pb-6 border-b border-white/20">
                      <h4 className="text-xl font-bold text-white mb-3">🏆 How does Colorado First Plumbing and Sewer LLC demonstrate honesty and transparency compared to other companies?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        Colorado First Plumbing and Sewer LLC stands out by being the only plumbing company with numerous verified reviews, real job content, actual Denver water heater installation photos, and visible, flat-rate sewer and plumbing prices posted directly on our website. Unlike other companies that may use misleading language, ranges, or hidden fees for "code required items" or permits, we provide clear, out-the-door pricing.
                      </p>
                    </div>

                    {/* Question 3 - Sewer Replacement Methods */}
                    <div className="mb-8 pb-6 border-b border-white/20">
                      <h4 className="text-xl font-bold text-white mb-3">🔧 What methods do you use to replace sewer lines in the Denver Metro Area?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        Colorado First Plumbing uses various forms of Sewer Replacement Methods in the Denver Areas such as Traditional Excavation, and Pipe Bursting.
                      </p>
                    </div>

                    {/* Question 4 - Traditional Sewer Replacement */}
                    <div className="mb-8 pb-6 border-b border-white/20">
                      <h4 className="text-xl font-bold text-white mb-3">🚜 What is the traditional way of replacing a outside sewer main in Denver?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        This method is the most common method for replacing Outside Sewer Mains in Denver. We use our excavator to carefully dig a trench typically from the home foundation to the street tap. We sawcut the asphalt in the street, and also use other equipment such as hydraulic shoring to protect the plumber during the replacement process. After we have passed a denver sewer inspection, we backfill the newly installed PVC Piping or SDR-35 Sewer Piping and repour the asphalt in the street.
                      </p>
                    </div>

                    {/* Question 5 - Pipe Bursting Method */}
                    <div className="mb-8 pb-6 border-b border-white/20">
                      <h4 className="text-xl font-bold text-white mb-3">💥 What is the Pipe-Bursting sewer replacement method?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        Pipe bursting is a specialized sewer replacement method which allows the entire outside sewer main to be replaced with typically 2 excavation areas, instead of excavating the entire line. We normally excavate 1 area near the outside foundation of the home (this is where the double cleanout and connection to the home will be installed), then excavate the 2nd area at the City Tap connection in the Street. Our hydraulic machine or excavator then pulls the new HDPE pipe, while breaking apart the old cast iron or clay piping. This new Piping is connected to the new tap and the home foundation pipe. After Inspection, we backfill the 2 excavation points and perform the street patch.
                      </p>
                    </div>

                    {/* Question 6 - Pipe Bursting Restrictions */}
                    <div className="mb-8">
                      <h4 className="text-xl font-bold text-white mb-3">⚠️ Are there any restrictions on Pipe-Bursting in Denver or surrounding areas?</h4>
                      <p className="text-blue-100 text-lg leading-relaxed mb-4">
                        Pipe Bursting is accepted in the majority of jurisdictions in Colorado. Denver City and Denver County however, have strict guidelines and regulations when it comes to the use of pipe-bursting. The sewer plan/permit has to be submitted and approved before any such pipe bursting work can be started. Other areas are not as strict such as Lakewood, Littleton, and Highlands Ranch. Furthermore, if a pipe has bellies or lowspots, then pipe bursting is not recommended. We can provide you with the best replacement option for your Denver sewer replacement.
                      </p>
                    </div>

                    {/* Call to Action Buttons */}
                    <div className="text-center space-y-4">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
                        
                        <button
                          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-xl hover:scale-[1.02] text-white px-6 py-3 rounded-full font-bold transition-all duration-300"
                        >
                          📝 Submit Sewer Form
                        </button>
                        
                      </div>
                      <a 
                        href="/sewer-replacement-spot-repair-pricing"
                        className="bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 inline-block"
                      >
                        🚰 See Full Sewer Pricing
                      </a>
                    </div>
                  </div>
                </div>

                {/* Images Side */}
                <div className="space-y-6">
                  <img
                    src="/page-specific-content/centennial-plumber/sewer-scope-double-cleanout-access.jpg"
                    alt="Professional sewer camera inspection and locate service"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                    loading="lazy"
                  />
                  <img
                    src="/colorado-plumbing-uploads/services/sewer-services/sewer-spot-repair.JPEG"
                    alt="Expert sewer spot repair in residential yard"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                    loading="lazy"
                  />
                  <img
                    src="/colorado-plumbing-uploads/services/sewer-services/excavation-replace-sewer-expert.JPEG"
                    alt="Professional sewer line replacement and excavation"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                    loading="lazy"
                  />
                  <img
                    src="/colorado-plumbing-uploads/locations/denver/sewer-outside-reapair-double-cleanout-denver.JPEG"
                    alt="Sewer outside repair with double cleanout installation in Denver"
                    className="w-full rounded-2xl shadow-xl border border-white/20"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trusted by over 7,000+ Coloradans Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-800 to-blue-900 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 font-bold text-sm border border-yellow-200 shadow-lg">
                ⭐ COLORADO'S TRUSTED CHOICE
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                Trusted by over <span className="text-yellow-300">7,000+</span> Coloradans
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                From homeowners to contractors, property managers to building inspectors - Colorado residents trust our transparent pricing and master plumber expertise.
              </p>
              
              {/* Trust Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="text-3xl font-bold text-yellow-300 mb-2">360</div>
                  <div className="text-blue-100 font-semibold">Google Reviews</div>
                  <div className="text-sm text-blue-200">4.8★ Average Rating</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="text-3xl font-bold text-yellow-300 mb-2">18,000+</div>
                  <div className="text-blue-100 font-semibold">Jobs Completed</div>
                  <div className="text-sm text-blue-200">Since 2018</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="text-3xl font-bold text-yellow-300 mb-2">30+</div>
                  <div className="text-blue-100 font-semibold">Jurisdictions Licensed</div>
                  <div className="text-sm text-blue-200">Colorado Licensed</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="text-3xl font-bold text-yellow-300 mb-2">ONLY</div>
                  <div className="text-blue-100 font-semibold">Exact Prices Online</div>
                  <div className="text-sm text-blue-200">No Hidden Fees</div>
                </div>
              </div>
              
              {/* Final CTA */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                
                
              </div>
            </div>
          </div>
        </section>
        
        {/* Gallery - Work portfolio */}
        <Gallery />
        
        {/* Service Area - Location-based trust */}
        <ServiceArea />
        
        {/* Traditional Testimonials - Written reviews */}
        <Testimonials />
        
        {/* Social Media - Additional social proof */}
        <SocialMedia />
        
        {/* Price Guarantee Section */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="inline-block px-6 py-3 mb-6 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 font-bold text-sm border border-green-200 shadow-lg">
                💰 OUR PRICE GUARANTEE
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
                Our Price Guarantee
              </h2>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="space-y-8">
                  {/* Guarantee Point 1 */}
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                      <span className="text-yellow-300 mr-3">1.</span> See Our Exact Prices Online
                    </h3>
                    <p className="text-blue-100 text-lg leading-relaxed mb-4">
                      Colorado First Plumbing and Sewer LLC is Colorado's ONLY plumber showing exact, flat-rate prices online. No ranges, no estimates, no surprises.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                        <div className="text-yellow-300 font-bold mb-2">Water Heater</div>
                        <div className="text-2xl font-bold text-white">$2,000</div>
                        <div className="text-sm text-blue-200">40 Gallon Special</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                        <div className="text-yellow-300 font-bold mb-2">Sewer Scope</div>
                        <div className="text-2xl font-bold text-white">$175</div>
                        <div className="text-sm text-blue-200">Camera & Locate</div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                        <div className="text-yellow-300 font-bold mb-2">Tankless</div>
                        <div className="text-2xl font-bold text-white">$5,000</div>
                        <div className="text-sm text-blue-200">Replacement</div>
                      </div>
                    </div>
                  </div>

                  {/* Guarantee Point 2 */}
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                      <span className="text-yellow-300 mr-3">2.</span> With Inclusions and Exclusions. No Hidden Fees.
                    </h3>
                    <p className="text-blue-100 text-lg leading-relaxed mb-4">
                      All services that are shown with pricing display exactly what the service comes with and what is additional if anything. Services like Water Heater Replacements and Sewer Replacements come with labor, materials, and permits included. Faucet pricing is without material because faucets vary depending on what you have or want.
                    </p>
                    <div className="bg-green-500/20 border border-green-400/30 rounded-lg p-4">
                      <p className="text-green-100 font-semibold">
                        ✅ What You See = What You Pay. No surprise charges, no after-the-fact markups, no sales games.
                      </p>
                    </div>
                  </div>

                  {/* Guarantee Point 3 */}
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                      <span className="text-yellow-300 mr-3">3.</span> Estimate Only Needed for Extraordinary Services or Projects
                    </h3>
                    <p className="text-blue-100 text-lg leading-relaxed">
                      For 95% of our services, you'll see the exact price online. Only complex custom projects like whole-home repiping or unique commercial installations require an on-site estimate - and we'll tell you that upfront.
                    </p>
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-8 text-center">
                  <a 
                    href="/pricing"
                    className="bg-gradient-to-r from-yellow-300 to-yellow-400 hover:shadow-xl hover:scale-[1.02] text-blue-900 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 inline-block"
                  >
                    💰 View All Guaranteed Prices
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact - Final conversion opportunity */}
        <Contact />
      </main>
      <Footer />
      <FloatingCTA />
      <Toaster />
    </div>
  );
};

export default Index;
